import type { GetIssueAssessmentByParentIdQuery } from '@risksmart-app/web-graphql-client/generated/graphql';
import { Parent_Type_Enum } from '@risksmart-app/web-graphql-client/generated/graphql';

type Parent = {
  IssueId: string;
  ParentId: string;
  ParentType: Parent_Type_Enum;
};

export const getAssociatedControlIds = (
  data: GetIssueAssessmentByParentIdQuery | undefined
) =>
  data?.issue_parent
    .filter((i) => i.parent?.ObjectType === Parent_Type_Enum.Control)
    .map((i) => i.ParentId) ?? [];

/**
 * Maps a multiselect's `{ value }[]` form field to a plain id array for the
 * save payload, falling back to the currently-loaded ids when the field is
 * absent.
 *
 * The `…Ids` link fields are TYPED as non-null arrays, but at submit they can
 * be `null` when their multiselect isn't the mounted control — e.g. a breach
 * flag is off, or the obligation/document/regulatory_breaches module is
 * disabled so the plain-text variant renders instead (WOR-130). A bare
 * `field.map(...)` then throws `Cannot read properties of null (reading 'map')`
 * client-side, before the mutation fires. Falling back to `fallback` (the
 * loaded ids) rather than `[]` also means a status-only save never silently
 * drops existing associations via the backend relationship diff-sync. A
 * genuine user-cleared multiselect submits `[]` (not null), so intentional
 * clears are preserved.
 */
export const resolveLinkIdValues = (
  field: { value: string }[] | null | undefined,
  fallback: string[]
): string[] => field?.map((a) => a.value) ?? fallback;

export const mapParentsToIds = (
  parents: Parent[],
  objectType: Parent_Type_Enum
) => {
  const ids =
    parents
      .filter((parent) => parent.ParentType === objectType)
      .map(({ ParentId }: { ParentId: string }) => ({
        value: ParentId,
      })) ?? [];

  return ids;
};
